// LayoutModel.js

/**
 * LayoutModel.js
 *
 * Document-shape helpers for type-scoped page templates (_layouts).
 */

import { Timestamp } from 'firebase/firestore';

import { withBaseKey } from 'utilities/basePersistence';
import { generateKey } from 'utilities/Keys';
import { normalizePublicPath } from 'utilities/PublicPaths';

const DEFAULT_LAYOUT_TITLE = 'Untitled Page';

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

export function getLayoutPagePath(layout) {
  return normalizePublicPath(layout?.suggestedPagePath || layout?.title) || 'page';
}

export function deriveLayoutPagePathFromTitle(title) {
  return normalizePublicPath(title) || 'page';
}

export function isDuplicateLayoutPagePathAmongLayouts(layouts, path, excludeLayoutKey = '') {
  const normalizedCandidate = normalizePublicPath(path);
  const normalizedExcludeLayoutKey = normalizeString(excludeLayoutKey);

  if (!normalizedCandidate) {
    return false;
  }

  return (Array.isArray(layouts) ? layouts : []).some((layout) => {
    const layoutKey = normalizeString(layout?.key);
    if (!layoutKey || layoutKey === normalizedExcludeLayoutKey) {
      return false;
    }

    return getLayoutPagePath(layout) === normalizedCandidate;
  });
}

export function layoutHasDiscussionBlock(blocksByKey) {
  const blocks = blocksByKey && typeof blocksByKey === 'object' && !Array.isArray(blocksByKey)
    ? blocksByKey
    : {};

  return Object.values(blocks).some((block) => (
    normalizeString(block?.type).toUpperCase() === 'DISCUSSION'
  ));
}

export function normalizeLayoutTitle(title) {
  return normalizeString(title) || DEFAULT_LAYOUT_TITLE;
}

export function buildDefaultLayoutTree(blockKey = null) {
  const rowKey = generateKey();
  const slotKey = generateKey();

  return {
    rootNodeKey: 'layout_root',
    nodes: {
      layout_root: {
        key: 'layout_root',
        kind: 'stack',
        children: [rowKey],
      },
      [rowKey]: {
        key: rowKey,
        kind: 'row',
        children: [slotKey],
      },
      [slotKey]: {
        key: slotKey,
        kind: 'block_slot',
        blockKey: normalizeString(blockKey) || null,
      },
    },
  };
}

export function buildDefaultLayoutScaffold() {
  return {
    blocksByKey: {},
    layout: buildDefaultLayoutTree(),
  };
}

function getValidBlocksByKey(blocksByKey) {
  return blocksByKey && typeof blocksByKey === 'object' && !Array.isArray(blocksByKey)
    ? blocksByKey
    : {};
}

function getValidLayout(layout) {
  return layout && typeof layout === 'object' && !Array.isArray(layout)
    ? layout
    : buildDefaultLayoutTree();
}

export function ensureLayoutHasAssignedBlock({ blocksByKey, layout } = {}) {
  const resolvedBlocksByKey = getValidBlocksByKey(blocksByKey);
  const resolvedLayout = getValidLayout(layout);
  const hasBlockSlot = Object.values(resolvedLayout?.nodes || {})
    .some((node) => node?.kind === 'block_slot');

  if (hasBlockSlot) {
    return {
      blocksByKey: resolvedBlocksByKey,
      layout: resolvedLayout,
    };
  }

  return buildDefaultLayoutScaffold();
}

export function normalizeLayoutRow(docSnap) {
  const data = docSnap.data() || {};

  return {
    id: docSnap.id,
    ...data,
    key: data?.key || docSnap.id,
  };
}

export function buildLayoutPayload({
  key,
  baseKey,
  typeKey,
  userKey,
  title,
  description = '',
  iconKey = '',
  position = null,
  suggestedPagePath = '',
  suggestedDiscussionEnabled = true,
  layout = null,
  blocksByKey = null,
} = {}) {
  const cleanedBaseKey = normalizeString(baseKey);
  const cleanedTypeKey = normalizeString(typeKey);
  const cleanedUserKey = normalizeString(userKey);

  if (!cleanedBaseKey || !cleanedTypeKey || !cleanedUserKey) {
    return null;
  }

  const layoutKey = normalizeString(key) || generateKey();
  const resolvedTitle = normalizeLayoutTitle(title);
  const resolvedDescription = normalizeString(description);
  const resolvedIconKey = normalizeString(iconKey) || 'public';
  const resolvedSuggestedPagePath = normalizePublicPath(suggestedPagePath)
    || deriveLayoutPagePathFromTitle(resolvedTitle);
  const numericPosition = Number(position);
  const { blocksByKey: resolvedBlocksByKey, layout: resolvedLayout } = ensureLayoutHasAssignedBlock({
    blocksByKey,
    layout,
  });
  const now = Timestamp.now();

  return withBaseKey({
    key: layoutKey,
    typeKey: cleanedTypeKey,
    title: resolvedTitle,
    titleLower: resolvedTitle.toLowerCase(),
    description: resolvedDescription,
    iconKey: resolvedIconKey,
    ...(Number.isFinite(numericPosition) ? { position: numericPosition } : {}),
    suggestedPagePath: resolvedSuggestedPagePath,
    suggestedDiscussionEnabled: suggestedDiscussionEnabled !== false,
    blocksByKey: resolvedBlocksByKey,
    layout: resolvedLayout,
    createdBy: cleanedUserKey,
    updatedBy: cleanedUserKey,
    dateCreated: now,
    dateModified: now,
  }, cleanedBaseKey);
}
